import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GalleryFolderMapper } from '@/components/admin/GalleryFolderMapper';
import { getAllUnitsForAdmin } from '@/lib/admin/units';
import {
  loadFolderList,
  parseMappingsJson,
  saveMappingsAsDownload,
  validateMappings,
  type UnitGalleryMappings,
} from '@/lib/admin/gallery-import';
import {
  suggestMappingsForAllFolders,
  type FolderMappingSuggestion,
} from '@/lib/admin/gallery-mapping';
import type { PropertyUnit } from '@/lib/units';
import { useToast } from '@/hooks/use-toast';
import {
  FolderOpen,
  Download,
  Upload,
  RefreshCw,
  AlertCircle,
  Loader2,
  FileCode,
} from 'lucide-react';

export default function AdminGalleryImport() {
  const [folders, setFolders] = useState<string[]>([]);
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [mappings, setMappings] = useState<UnitGalleryMappings>({});
  const [suggestions, setSuggestions] = useState<FolderMappingSuggestion[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    const list = await loadFolderList();
    setFolders(list);
    setLoadingFolders(false);
  }, []);

  const loadUnits = useCallback(async () => {
    setLoadingUnits(true);
    const { data, error } = await getAllUnitsForAdmin();
    if (error) {
      toast({
        title: 'Error loading units',
        description: error,
        variant: 'destructive',
      });
    }
    setUnits(data || []);
    setLoadingUnits(false);
  }, [toast]);

  useEffect(() => {
    loadFolders();
    loadUnits();
  }, [loadFolders, loadUnits]);

  useEffect(() => {
    if (folders.length > 0 && units.length > 0) {
      setSuggestions(suggestMappingsForAllFolders(folders, units));
    }
  }, [folders, units]);

  const handleSelectionChange = (folderName: string, unitIds: string[]) => {
    setMappings((prev) => ({
      ...prev,
      [folderName]: unitIds,
    }));
  };

  const handleLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = reader.result as string;
        const parsed = parseMappingsJson(json);
        setMappings(parsed);
        toast({
          title: 'Mappings loaded',
          description: `Loaded ${Object.keys(parsed).length} folder(s).`,
        });
      } catch (err) {
        toast({
          title: 'Invalid file',
          description: err instanceof Error ? err.message : 'Could not parse JSON',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSave = () => {
    const { valid, errors } = validateMappings(mappings);
    if (!valid) {
      toast({
        title: 'Cannot save',
        description: errors.join('. '),
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    const toSave: UnitGalleryMappings = {};
    for (const folderName of folders) {
      toSave[folderName] = mappings[folderName] ?? [];
    }
    saveMappingsAsDownload(toSave);
    toast({
      title: 'Mappings downloaded',
      description: 'Save the file as data/master/unit-gallery-mappings.json and run: npm run process-gallery',
    });
    setSaving(false);
  };

  const validation = validateMappings(mappings);
  const unmappedCount = folders.filter((f) => !mappings[f]?.length).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Unit Gallery Import</h2>
        <p className="text-muted-foreground">
          Map unit-gallery folders to units, then save the mapping file and run the processing script to optimize and upload images.
        </p>
      </div>

      <Alert>
        <FolderOpen className="h-4 w-4" />
        <AlertDescription>
          <strong>Folder list:</strong> Run <code className="rounded bg-muted px-1">npm run list-gallery-folders</code> to
          refresh the list from <code className="rounded bg-muted px-1">data/master/unit-gallery/</code>. Then reload this page.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Folder to unit mapping</CardTitle>
          <CardDescription>
            Map each gallery folder to one or more units. Use &quot;Apply&quot; to accept auto-detected suggestions, or select units manually.
          </CardDescription>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadFolders}
              disabled={loadingFolders}
            >
              {loadingFolders ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh folders
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('load-mappings-input')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Load mapping file
            </Button>
            <input
              id="load-mappings-input"
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleLoadFile}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving || !validation.valid}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download mappings
            </Button>
          </div>
          {!validation.valid && Object.keys(mappings).length > 0 && (
            <Alert variant="destructive" className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validation.errors.join(' ')}</AlertDescription>
            </Alert>
          )}
          {unmappedCount > 0 && folders.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {unmappedCount} folder(s) without mapping. Save the file as{' '}
              <code className="rounded bg-muted px-1">data/master/unit-gallery-mappings.json</code> then run{' '}
              <code className="rounded bg-muted px-1">npm run process-gallery</code>.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingFolders || loadingUnits ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading folders and units…
            </div>
          ) : folders.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <FileCode className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No folder list found. Run <code>npm run list-gallery-folders</code> then refresh.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {folders.map((folderName) => (
                <li key={folderName}>
                  <GalleryFolderMapper
                    folderName={folderName}
                    suggestion={suggestions.find((s) => s.folderName === folderName) ?? { folderName, suggestedUnitIds: [], extractedIdentifiers: [], confidence: 'none' }}
                    selectedUnitIds={mappings[folderName] ?? []}
                    units={units}
                    onSelectionChange={(unitIds) => handleSelectionChange(folderName, unitIds)}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
